import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import useTranslation from '@/hooks/useTranslation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { getProviderStats, getManufacturerStats, getMyCompany, getSavedSuppliers, getMyGivenRatings } from '@/lib/api';
import OnboardingBanner from '@/components/OnboardingBanner';
import ProPaywall from '@/components/ProPaywall';
import ReferralWidget from '@/components/ReferralWidget';
import {
  Package,
  FileText,
  TestTube,
  Ship,
  ShoppingCart,
  MessageSquare,
  Eye,
  TrendingUp,
  ArrowRight,
  Building2,
  Plus,
  Bookmark,
  CheckCircle2,
  Zap,
  Target,
  Clock,
  AlertCircle,
  Star,
  Factory,
  Users,
  DollarSign,
  BarChart3,
  Heart,
  Gift,
  Crown,
  Sparkles,
  Store
} from 'lucide-react';

const DashboardHome = () => {
  const { user, token } = useAuth();
  const { isUSA, formatPrice } = useLocale();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedSuppliers, setSavedSuppliers] = useState([]);
  const [myRatings, setMyRatings] = useState([]);
  const [promoStatus, setPromoStatus] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const fetchData = useCallback(async () => {
    // Wait for token to be available
    const authToken = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
    if (!authToken) {
      setLoading(false);
      return;
    }
    
    try {
      // Fetch promo status for Oro users
      try {
        const promoRes = await fetch(`${API_URL}/api/promo/my-status`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (promoRes.ok) {
          const promoData = await promoRes.json();
          setPromoStatus(promoData);
        }
      } catch (e) {
        console.error('Error fetching promo status:', e);
      }

      // Fetch account status (limits and usage)
      try {
        const accountRes = await fetch(`${API_URL}/api/subscriptions/account-status`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (accountRes.ok) {
          const accountData = await accountRes.json();
          setAccountStatus(accountData);
          
          // Show upgrade modal if user has reached inquiry threshold
          if (!accountData.is_pro && accountData.limits?.inquiries?.show_upgrade_prompt) {
            // Check if we've already shown this modal in this session
            const shownKey = 'texhub_inquiry_upgrade_shown';
            if (!sessionStorage.getItem(shownKey)) {
              setShowUpgradeModal(true);
              sessionStorage.setItem(shownKey, 'true');
            }
          }
        }
      } catch (e) {
        console.error('Error fetching account status:', e);
      }

      try {
        const companyData = await getMyCompany();
        setCompany(companyData);
      } catch (e) {
        // No company yet
      }

      if (user?.company_id) {
        try {
          const providerStats = await getProviderStats();
          const manufacturerStats = await getManufacturerStats();
          setStats({ ...providerStats, ...manufacturerStats });
          
          // Get saved suppliers for manufacturers
          const saved = await getSavedSuppliers();
          setSavedSuppliers(saved.slice(0, 5));
          
          // Get ratings given
          try {
            const ratings = await getMyGivenRatings();
            setMyRatings(ratings.slice(0, 3));
          } catch (e) {}
        } catch (e) {
          console.error('Error fetching stats:', e);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.company_id, API_URL]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  // Check if user is a buyer (limited access) - check BEFORE company check
  const isBuyer = user?.account_type === 'buyer';

  // Special dashboard for buyers (they don't need a company)
  if (isBuyer) {
    return (
      <div className="space-y-6" data-testid="buyer-dashboard">
        {/* Welcome Banner */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope, sans-serif'}}>
            {isUSA ? `Welcome back, ${user?.name?.split(' ')[0]}!` : `¡Hola de nuevo, ${user?.name?.split(' ')[0]}!`}
          </h1>
          <p className="text-slate-600">
            {isUSA 
              ? 'Explore stores and manage your orders'
              : 'Explora tiendas y gestiona tus pedidos'
            }
          </p>
        </div>

        {/* Upgrade to Seller Banner */}
        <Card className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-6 h-6" />
                  <span className="font-bold text-lg">
                    {isUSA ? 'Want to sell online?' : '¿Quieres vender en línea?'}
                  </span>
                </div>
                <p className="text-emerald-100 mb-4">
                  {isUSA 
                    ? 'Create your own online store and reach thousands of buyers. Start selling today!'
                    : 'Crea tu propia tienda en línea y llega a miles de compradores. ¡Empieza a vender hoy!'
                  }
                </p>
                <div className="flex flex-wrap gap-4 text-sm text-emerald-100 mb-4">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> {isUSA ? 'Free online store' : 'Tienda en línea gratis'}
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> {isUSA ? 'Inventory management' : 'Gestión de inventario'}
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> {isUSA ? 'Direct orders' : 'Pedidos directos'}
                  </span>
                </div>
                <Link to="/register-company">
                  <Button className="bg-white text-emerald-600 hover:bg-emerald-50">
                    <Sparkles className="w-4 h-4 mr-2" />
                    {isUSA ? 'Upgrade to Seller' : 'Convertirme en Vendedor'}
                  </Button>
                </Link>
              </div>
              <div className="hidden md:block">
                <Store className="w-32 h-32 text-emerald-300 opacity-50" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/tiendas">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-emerald-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Store className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      {isUSA ? 'Explore Stores' : 'Explorar Tiendas'}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {isUSA ? 'Find products from verified sellers' : 'Encuentra productos de vendedores verificados'}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/dashboard/store-orders">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <ShoppingCart className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      {isUSA ? 'My Orders' : 'Mis Pedidos'}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {isUSA ? 'Track and manage your orders' : 'Rastrea y gestiona tus pedidos'}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Browse Categories */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {isUSA ? 'Browse by Category' : 'Explorar por Categoría'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: isUSA ? 'Fabrics' : 'Telas', icon: '🧵', path: '/tiendas?category=telas' },
                { label: isUSA ? 'Clothing' : 'Ropa', icon: '👕', path: '/tiendas?category=ropa' },
                { label: isUSA ? 'Accessories' : 'Accesorios', icon: '👜', path: '/tiendas?category=accesorios' },
                { label: isUSA ? 'All Stores' : 'Todas las Tiendas', icon: '🏪', path: '/tiendas' },
              ].map((cat, idx) => (
                <Link key={idx} to={cat.path}>
                  <div className="p-4 text-center rounded-lg border hover:border-emerald-300 hover:bg-emerald-50 transition-colors cursor-pointer">
                    <span className="text-2xl mb-2 block">{cat.icon}</span>
                    <span className="text-sm font-medium text-slate-700">{cat.label}</span>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Onboarding for new SELLER users without company (not buyers)
  if (!company) {
    const steps = isUSA ? [
      { done: true, label: "Create account", description: "Done!" },
      { done: false, label: "Register company", description: "Just 2 minutes" },
      { done: false, label: "Start connecting", description: "Get quotes or sell" }
    ] : [
      { done: true, label: "Crear cuenta", description: "¡Ya lo hiciste!" },
      { done: false, label: "Registrar empresa", description: "Solo 2 minutos" },
      { done: false, label: "Empezar a conectar", description: "Cotiza o vende" }
    ];

    return (
      <div className="max-w-3xl mx-auto" data-testid="onboarding">
        {/* Welcome */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope, sans-serif'}}>
            {isUSA ? `Welcome to TexHub, ${user?.name?.split(' ')[0]}!` : `¡Bienvenido a TexHub, ${user?.name?.split(' ')[0]}!`}
          </h1>
          <p className="text-slate-600">
            {isUSA 
              ? 'Just one more step to start connecting with the textile industry'
              : 'Solo falta un paso para empezar a conectar con la industria textil'
            }
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  step.done ? 'bg-emerald-600 text-white' : index === 1 ? 'bg-emerald-100 text-emerald-600 border-2 border-emerald-600' : 'bg-slate-100 text-slate-400'
                }`}>
                  {step.done ? <CheckCircle2 className="w-5 h-5" /> : index + 1}
                </div>
                <p className={`text-sm mt-2 font-medium ${step.done ? 'text-emerald-600' : index === 1 ? 'text-slate-900' : 'text-slate-400'}`}>
                  {step.label}
                </p>
              </div>
              {index < steps.length - 1 && (
                <div className={`w-16 h-0.5 ${step.done ? 'bg-emerald-600' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Main CTA Card */}
        <Card className="bg-white border-2 border-emerald-200 shadow-lg">
          <CardContent className="p-4 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0 w-full">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {isUSA ? 'Register your company' : 'Registra tu empresa'}
                </h2>
                <p className="text-sm sm:text-base text-slate-600 mb-4">
                  {isUSA 
                    ? 'For other businesses to find you, we need some basic information about your company.'
                    : 'Para que otros negocios te encuentren, necesitamos algunos datos básicos de tu empresa.'
                  }
                  <span className="font-medium text-slate-900"> {isUSA ? 'Only takes 2 minutes.' : 'Solo toma 2 minutos.'}</span>
                </p>
                
                <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6">
                  <Badge variant="outline" className="text-slate-600 text-xs sm:text-sm">
                    <Clock className="w-3 h-3 mr-1" />
                    {isUSA ? '2 minutes' : '2 minutos'}
                  </Badge>
                  <Badge variant="outline" className="text-slate-600 text-xs sm:text-sm">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {isUSA ? 'No commitments' : 'Sin compromisos'}
                  </Badge>
                  <Badge variant="outline" className="text-slate-600 text-xs sm:text-sm">
                    <Target className="w-3 h-3 mr-1" />
                    {isUSA ? 'Edit anytime' : 'Puedes editar después'}
                  </Badge>
                </div>

                <Link to="/dashboard/company/new">
                  <Button className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 h-11 sm:h-12 px-4 sm:px-6 text-sm sm:text-base font-semibold" data-testid="register-company-btn">
                    {isUSA ? 'Register my company now' : 'Registrar mi empresa ahora'}
                    <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Benefits Preview */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-4 text-center">
              <Zap className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <p className="font-medium text-slate-900">{isUSA ? 'Receive quote requests' : 'Recibe cotizaciones'}</p>
              <p className="text-sm text-slate-500">{isUSA ? 'From verified buyers' : 'De compradores verificados'}</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-4 text-center">
              <Eye className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="font-medium text-slate-900">{isUSA ? '24/7 Catalog' : 'Catálogo 24/7'}</p>
              <p className="text-sm text-slate-500">{isUSA ? 'Visible to the entire industry' : 'Visible para toda la industria'}</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="font-medium text-slate-900">{isUSA ? 'Grow your business' : 'Crece tu negocio'}</p>
              <p className="text-sm text-slate-500">{isUSA ? 'Without additional salespeople' : 'Sin vendedores adicionales'}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Calculate profile completeness
  const profileFields = [
    company.company_name,
    company.description,
    company.phone,
    company.email,
    company.logo_url,
    company.certifications?.length > 0,
    company.production_capacity
  ];
  const completedFields = profileFields.filter(Boolean).length;
  const profileProgress = Math.round((completedFields / profileFields.length) * 100);

  return (
    <div data-testid="dashboard-home">
      {/* Onboarding Banner */}
      <OnboardingBanner />

      {/* Welcome Header */}
      <div className="mb-8" data-testid="dashboard-welcome">
        <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Manrope, sans-serif'}}>
          {isUSA ? 'Hello' : 'Hola'}, {user?.name?.split(' ')[0]} 👋
        </h1>
        <p className="text-slate-600 mt-1">
          {isUSA ? "Here's your summary for today" : 'Aquí está tu resumen de hoy'}
        </p>
      </div>

      {/* Profile Completeness Alert */}
      {profileProgress < 70 && (
        <Card className="bg-amber-50 border-amber-200 mb-6">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">
                  {isUSA 
                    ? `Your profile is ${profileProgress}% complete - finish it to receive more quotes`
                    : `Tu perfil está al ${profileProgress}% - complétalo para recibir más cotizaciones`
                  }
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  {isUSA ? 'Complete profiles receive 3x more contacts' : 'Los perfiles completos reciben 3x más contactos'}
                </p>
                <Progress value={profileProgress} className="mt-3 h-2" />
              </div>
              <Link to="/dashboard/company">
                <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-100">
                  {isUSA ? 'Complete profile' : 'Completar perfil'}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Account Usage Metrics for Free Users */}
      {accountStatus && !accountStatus.is_pro && (
        <Card className="bg-slate-50 border-slate-200 mb-6" data-testid="account-usage-metrics">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-slate-600" />
              {isUSA ? 'Your Account' : 'Tu Cuenta'}
              <Badge variant="outline" className="ml-2 text-slate-500">
                {isUSA ? 'Free Plan' : 'Plan Gratis'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {/* Products Usage */}
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600 flex items-center gap-1">
                    <Package className="w-4 h-4" />
                    {isUSA ? 'Products' : 'Productos'}
                  </span>
                  <Badge variant={accountStatus.limits.products.can_add ? 'outline' : 'destructive'}>
                    {accountStatus.limits.products.used}/{accountStatus.limits.products.limit}
                  </Badge>
                </div>
                <Progress 
                  value={(accountStatus.limits.products.used / accountStatus.limits.products.limit) * 100} 
                  className={`h-2 ${accountStatus.limits.products.can_add ? '' : '[&>div]:bg-red-500'}`}
                />
                {accountStatus.limits.products.used >= accountStatus.limits.products.limit - 1 && accountStatus.limits.products.can_add && (
                  <p className="text-xs text-amber-600 mt-2">
                    {isUSA ? 'Almost at limit!' : '¡Casi al límite!'}
                  </p>
                )}
              </div>
              
              {/* Inquiries Received */}
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600 flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    {isUSA ? 'Inquiries' : 'Cotizaciones'}
                  </span>
                  <Badge variant="outline" className={accountStatus.limits.inquiries.show_upgrade_prompt ? 'bg-emerald-100 text-emerald-700' : ''}>
                    {accountStatus.limits.inquiries.received}
                  </Badge>
                </div>
                <Progress 
                  value={Math.min((accountStatus.limits.inquiries.received / accountStatus.limits.inquiries.threshold) * 100, 100)} 
                  className="h-2 [&>div]:bg-emerald-500"
                />
                {accountStatus.limits.inquiries.show_upgrade_prompt && (
                  <p className="text-xs text-emerald-600 mt-2 font-medium">
                    {isUSA ? "You're getting traction!" : '¡Estás generando interés!'}
                  </p>
                )}
              </div>
            </div>
            
            {/* Upgrade CTA */}
            <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
              <div className="text-sm text-slate-600">
                <span className="font-medium">{isUSA ? 'Unlock with PRO:' : 'Desbloquea con PRO:'}</span>
                <span className="ml-2">
                  {isUSA ? 'Online store, TexVision AI, Priority search' : 'Tienda en línea, TexVision AI, Prioridad en búsqueda'}
                </span>
              </div>
              <Link to="/dashboard/subscription">
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  {isUSA ? 'Upgrade' : 'Actualizar'}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Premium Promo Status Banner for Oro Users with Active Promo */}
      {/* Company Card */}
      <Card className="bg-white border-slate-200 mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {company.logo_url ? (
                <img 
                  src={company.logo_url} 
                  alt={company.company_name}
                  className="w-14 h-14 rounded-xl object-cover"
                />
              ) : (
                <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-slate-400" />
                </div>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-bold text-slate-900" style={{fontFamily: 'Manrope, sans-serif'}}>
                    {company.company_name}
                  </h2>
                  {company.verified && (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      {isUSA ? 'Verified' : 'Verificada'}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-slate-500">
                  {company.company_type === 'proveedor' 
                    ? (isUSA ? 'Supplier' : 'Proveedor')
                    : company.company_type === 'fabricante' 
                      ? (isUSA ? 'Manufacturer' : 'Fabricante') 
                      : (isUSA ? 'Supplier & Manufacturer' : 'Proveedor y Fabricante')}
                  {' · '}{company.location_city}, {company.location_state}
                </p>
              </div>
            </div>
            <Link to="/dashboard/company">
              <Button variant="outline" size="sm">
                {isUSA ? 'Edit' : 'Editar'}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid - Differentiated by Role */}
      {company.company_type === 'proveedor' || company.company_type === 'ambos' ? (
        <ProviderDashboard stats={stats} company={company} isUSA={isUSA} />
      ) : null}
      
      {company.company_type === 'fabricante' || company.company_type === 'ambos' ? (
        <ManufacturerDashboard stats={stats} savedSuppliers={savedSuppliers} myRatings={myRatings} isUSA={isUSA} />
      ) : null}

      {/* Top Products - Only for providers */}
      {(company.company_type === 'proveedor' || company.company_type === 'ambos') && 
       stats?.top_products && stats.top_products.length > 0 && (
        <Card className="bg-white border-slate-200 mt-8">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg" style={{fontFamily: 'Manrope, sans-serif'}}>
                {isUSA ? 'Your most viewed products' : 'Tus productos más vistos'}
              </CardTitle>
              <Link to="/dashboard/products">
                <Button variant="ghost" size="sm">
                  {isUSA ? 'View all' : 'Ver todos'}
                  <ArrowRight className="ml-1 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.top_products.map((product, index) => (
                <div 
                  key={product.product_id} 
                  className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-400 w-6">#{index + 1}</span>
                    <span className="font-medium text-slate-900">{product.name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-500">
                    <Eye className="w-4 h-4" />
                    <span className="text-sm font-medium">{product.views} {isUSA ? 'views' : 'vistas'}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== PROVIDER DASHBOARD ====================
const ProviderDashboard = ({ stats, company, isUSA }) => {
  const providerStats = [
    {
      title: isUSA ? 'Products published' : 'Productos publicados',
      value: stats?.total_products || 0,
      subtitle: isUSA ? 'in your catalog' : 'en tu catálogo',
      icon: <Package className="w-5 h-5" />,
      link: '/dashboard/products',
      color: 'bg-blue-50 text-blue-600',
      cta: stats?.total_products === 0 ? (isUSA ? 'Add product' : 'Agregar producto') : null
    },
    {
      title: isUSA ? 'Quotes received' : 'Cotizaciones recibidas',
      value: stats?.total_quotes_received || 0,
      pending: stats?.pending_quotes || 0,
      subtitle: stats?.pending_quotes > 0 
        ? (isUSA ? `${stats.pending_quotes} pending response` : `${stats.pending_quotes} pendientes por responder`)
        : (isUSA ? 'total requests' : 'total de solicitudes'),
      icon: <FileText className="w-5 h-5" />,
      link: '/dashboard/quotes',
      color: 'bg-purple-50 text-purple-600',
      urgent: stats?.pending_quotes > 0
    },
    {
      title: isUSA ? 'Pending samples' : 'Muestras pendientes',
      value: stats?.pending_samples || 0,
      subtitle: isUSA ? 'requests to send' : 'solicitudes por enviar',
      icon: <TestTube className="w-5 h-5" />,
      link: '/dashboard/samples',
      color: 'bg-orange-50 text-orange-600',
      urgent: stats?.pending_samples > 0
    },
    {
      title: isUSA ? 'New messages' : 'Mensajes nuevos',
      value: stats?.unread_messages || 0,
      subtitle: isUSA ? 'unread from clients' : 'sin leer de clientes',
      icon: <MessageSquare className="w-5 h-5" />,
      link: '/dashboard/messages',
      color: 'bg-rose-50 text-rose-600',
      urgent: stats?.unread_messages > 0
    }
  ];

  return (
    <>
      {/* Provider Stats */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-5 h-5" style={{ color: '#1E4C8E' }} />
          <h3 className="font-semibold text-slate-900" style={{fontFamily: 'Manrope, sans-serif'}}>
            {isUSA ? 'Your supplier activity' : 'Tu actividad como proveedor'}
          </h3>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {providerStats.map((stat, index) => (
            <Link key={index} to={stat.link}>
              <Card className={`bg-white border-slate-200 hover:border-slate-300 hover:shadow-md transition-all cursor-pointer h-full ${stat.urgent ? 'ring-2 ring-orange-200' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                      {stat.icon}
                    </div>
                    {stat.urgent && (
                      <Badge className="bg-orange-100 text-orange-700 text-xs">
                        {isUSA ? 'Pending' : 'Pendiente'}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm font-medium text-slate-600 mb-1">{stat.title}</p>
                  <div className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope, sans-serif'}}>
                    {stat.value}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{stat.subtitle}</p>
                  {stat.cta && (
                    <Button size="sm" className="mt-3 w-full" style={{ backgroundColor: '#34B772' }}>
                      {stat.cta}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* Provider Quick Actions */}
      <h3 className="font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>
        {isUSA ? 'Quick actions' : 'Acciones rápidas'}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Link to="/dashboard/products/new" data-testid="provider-add-product">
          <Card className="bg-white border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Plus className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">{isUSA ? 'Add product' : 'Agregar producto'}</h4>
                <p className="text-sm text-slate-500">{isUSA ? 'Expand your catalog' : 'Amplía tu catálogo'}</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/dashboard/surplus/new" data-testid="provider-add-surplus">
          <Card className="bg-white border-slate-200 hover:border-yellow-300 hover:shadow-md transition-all cursor-pointer">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">{isUSA ? 'Post surplus' : 'Publicar excedente'}</h4>
                <p className="text-sm text-slate-500">{isUSA ? 'Sell inventory fast' : 'Vende inventario rápido'}</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/dashboard/quotes" data-testid="provider-view-quotes">
          <Card className="bg-white border-slate-200 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">{isUSA ? 'Respond to requests' : 'Responder solicitudes'}</h4>
                <p className="text-sm text-slate-500">{isUSA ? 'Send quotes' : 'Envía cotizaciones'}</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Referral Widget for Providers */}
      <div className="mb-8">
        <ReferralWidget />
      </div>
    </>
  );
};

// ==================== MANUFACTURER DASHBOARD ====================
const ManufacturerDashboard = ({ stats, savedSuppliers, myRatings, isUSA }) => {
  const manufacturerStats = [
    {
      title: isUSA ? 'Requests sent' : 'Solicitudes enviadas',
      value: stats?.total_quotes_sent || 0,
      subtitle: isUSA ? 'awaiting quote' : 'esperando cotización',
      icon: <FileText className="w-5 h-5" />,
      link: '/dashboard/quotes',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      title: isUSA ? 'Saved suppliers' : 'Proveedores guardados',
      value: stats?.saved_suppliers || 0,
      subtitle: isUSA ? 'in your list' : 'en tu lista',
      icon: <Bookmark className="w-5 h-5" />,
      link: '/dashboard/saved',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      title: isUSA ? 'Samples requested' : 'Muestras solicitadas',
      value: stats?.total_samples_requested || 0,
      subtitle: isUSA ? 'this month' : 'este mes',
      icon: <TestTube className="w-5 h-5" />,
      link: '/dashboard/samples',
      color: 'bg-orange-50 text-orange-600'
    },
    {
      title: isUSA ? 'Messages' : 'Mensajes',
      value: stats?.unread_messages || 0,
      subtitle: isUSA ? 'unread' : 'sin leer',
      icon: <MessageSquare className="w-5 h-5" />,
      link: '/dashboard/messages',
      color: 'bg-rose-50 text-rose-600',
      urgent: stats?.unread_messages > 0
    }
  ];

  return (
    <>
      {/* Manufacturer Stats */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Factory className="w-5 h-5" style={{ color: '#1E4C8E' }} />
          <h3 className="font-semibold text-slate-900" style={{fontFamily: 'Manrope, sans-serif'}}>
            {isUSA ? 'Your buyer activity' : 'Tu actividad como comprador'}
          </h3>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {manufacturerStats.map((stat, index) => (
            <Link key={index} to={stat.link}>
              <Card className={`bg-white border-slate-200 hover:border-slate-300 hover:shadow-md transition-all cursor-pointer h-full ${stat.urgent ? 'ring-2 ring-orange-200' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                      {stat.icon}
                    </div>
                    {stat.urgent && (
                      <Badge className="bg-orange-100 text-orange-700 text-xs">
                        {isUSA ? 'New' : 'Nuevo'}
                      </Badge>
                    )}
                  </div>
                  <div className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope, sans-serif'}}>
                    {stat.value}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{stat.subtitle}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* Manufacturer Quick Actions */}
      <h3 className="font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>
        {isUSA ? 'Quick actions' : 'Acciones rápidas'}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Link to="/dashboard/providers" data-testid="manufacturer-find-providers">
          <Card className="bg-white border-slate-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">{isUSA ? 'Find suppliers' : 'Buscar proveedores'}</h4>
                <p className="text-sm text-slate-500">{isUSA ? 'Find new partners' : 'Encuentra nuevos aliados'}</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/surplus" data-testid="manufacturer-view-surplus">
          <Card className="bg-white border-slate-200 hover:border-yellow-300 hover:shadow-md transition-all cursor-pointer">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">{isUSA ? 'View surplus' : 'Ver excedentes'}</h4>
                <p className="text-sm text-slate-500">{isUSA ? 'Market opportunities' : 'Oportunidades del mercado'}</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/dashboard/imports/new" data-testid="manufacturer-imports">
          <Card className="bg-white border-slate-200 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Ship className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">{isUSA ? 'Schedule import' : 'Programar importación'}</h4>
                <p className="text-sm text-slate-500">{isUSA ? 'Imported fabric' : 'Tela de importación'}</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Referral Widget for Manufacturers */}
      <div className="mb-6">
        <ReferralWidget />
      </div>

      {/* Saved Suppliers Preview */}
      {savedSuppliers && savedSuppliers.length > 0 && (
        <Card className="bg-white border-slate-200 mb-6">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily: 'Manrope, sans-serif'}}>
                <Bookmark className="w-5 h-5" style={{ color: '#1E4C8E' }} />
                {isUSA ? 'Saved suppliers' : 'Proveedores guardados'}
              </CardTitle>
              <Link to="/dashboard/saved">
                <Button variant="ghost" size="sm">
                  {isUSA ? 'View all' : 'Ver todos'}
                  <ArrowRight className="ml-1 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {savedSuppliers.map((supplier, index) => (
                <Link 
                  key={supplier.company_id || index} 
                  to={`/company/${supplier.company_id}`}
                  className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 px-2 rounded"
                >
                  <div className="flex items-center gap-3">
                    {supplier.logo_url ? (
                      <img src={supplier.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                    ) : (
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-slate-400" />
                      </div>
                    )}
                    <div>
                      <span className="font-medium text-slate-900">{supplier.company_name}</span>
                      <p className="text-xs text-slate-500">{supplier.industry}</p>
                    </div>
                  </div>
                  {supplier.rating_average > 0 && (
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      {supplier.rating_average.toFixed(1)}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Ratings Given */}
      {myRatings && myRatings.length > 0 && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily: 'Manrope, sans-serif'}}>
              <Star className="w-5 h-5 text-yellow-500" />
              {isUSA ? 'Your recent ratings' : 'Tus calificaciones recientes'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {myRatings.map((rating, index) => (
                <div 
                  key={rating.rating_id || index} 
                  className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-slate-900">{rating.target_company_name}</span>
                    <Badge variant="outline" className="text-xs">
                      {rating.rating_type === 'provider' 
                        ? (isUSA ? 'Supplier' : 'Proveedor') 
                        : (isUSA ? 'Client' : 'Cliente')}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(star => (
                      <Star 
                        key={star} 
                        className={`w-4 h-4 ${star <= rating.average_rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'}`} 
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Referral Widget */}
      {/* Upgrade Modal for Inquiry Threshold */}
      <ProPaywall 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
        feature={isUSA ? 'continue receiving clients' : 'seguir recibiendo clientes'}
        reason="inquiries"
      />
    </>
  );
};

export default DashboardHome;
